/**
 * The CC list of a "one email, everyone in CC" campaign.
 *
 * One message goes to a single TO address and copies everybody else, so the CC list IS
 * the audience. It is assembled from three sources that must agree — the vendors picked
 * on the Audience step, a spreadsheet, and addresses typed by hand — and the provider
 * rejects the entire request if one address appears twice. Everything here is pure so
 * those rules are testable without a browser (ccList.test.js, `node --test`).
 *
 * An entry keeps `vendor_code` so the downloaded sheet can be re-uploaded: the backend
 * resolves coded rows against the live Vendor table and reads the Email column only for
 * rows that carry no code, which is what lets a hand-added address survive the trip.
 */

/** Permissive on purpose — it rejects paste-junk, not unusual but valid addresses. */
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

/** The address, trimmed — or '' when the value is not an address at all. */
export function normalizeAddress(raw) {
  const s = typeof raw === 'string' ? raw.trim() : '';
  return EMAIL_RE.test(s) ? s : '';
}

/**
 * Addresses out of free text. Operators paste from Outlook, from chat, from a sheet
 * column, so commas, semicolons and newlines all separate; junk is dropped rather than
 * failing the whole paste.
 */
export function parseAddresses(text) {
  const seen = new Set();
  const out = [];
  for (const part of String(text ?? '').split(/[\s,;]+/)) {
    const email = normalizeAddress(part);
    if (!email || seen.has(email.toLowerCase())) continue;
    seen.add(email.toLowerCase());
    out.push(email);
  }
  return out;
}

/** A vendor row (audience list or spreadsheet import) as a CC entry. */
export function toCcEntry(v) {
  return {
    email: String(v?.email ?? '').trim(),
    label: String(v?.company_name ?? '').trim(),
    vendor_code: v?.vendor_code ?? null,
  };
}

/**
 * Append `incoming` to `existing`, dropping anything already present and anything that
 * is not an address. Case-insensitive, because the provider's own uniqueness check is,
 * and first-in wins so a vendor keeps its company label when the same address arrives
 * again from a sheet with no company against it. Order is stable: the list must not
 * reshuffle under the operator while they are editing it.
 */
export function mergeCc(existing, incoming) {
  const out = [...(existing ?? [])];
  const seen = new Set(out.map((e) => String(e.email).toLowerCase()));
  for (const raw of incoming ?? []) {
    const email = normalizeAddress(raw?.email);
    if (!email || seen.has(email.toLowerCase())) continue;
    seen.add(email.toLowerCase());
    out.push({ email, label: raw.label ?? '', vendor_code: raw.vendor_code ?? null });
  }
  return out;
}

/** The CC list as sheet rows. Header names match what the importer looks for. */
export function ccSheetAoA(entries) {
  return [
    ['Vendor Code', 'Company', 'Email'],
    ...(entries ?? []).map((e) => [e.vendor_code ?? '', e.label ?? '', e.email]),
  ];
}
